import { type KeyboardEvent as ReactKeyboardEvent, useRef, useState } from "react";

// One tab stop for a grid of buttons, with the arrow keys moving between the ones inside it, which is what a
// toolbar's roles promise. A key nobody can press is stepped over rather than landed on, since a stop there is a
// dead end. `/calculator` reaches every key of two pads and every bit of a word through this, and `/unicode` every
// character of a block, which is the same complaint answered once: forty tab stops is bad and two hundred is worse.
export function useRovingFocus(count: number, columns: number, enabled: (index: number) => boolean) {
  const [focused, setFocused] = useState(0);
  const nodes = useRef<(HTMLElement | null)[]>([]);

  const search = (from: number, stride: number): number | null => {
    for (let index = from; index >= 0 && index < count; index += stride) {
      if (enabled(index)) return index;
    }
    return null;
  };

  // Where a Tab into the pad lands: the remembered key while it still exists and can be pressed, the nearest otherwise.
  const start = Math.min(focused, count - 1);
  const active = search(start, 1) ?? search(start, -1) ?? 0;

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    const strides: Record<string, number> = { ArrowRight: 1, ArrowLeft: -1, ArrowDown: columns, ArrowUp: -columns };
    const stride = strides[event.key];
    const target = stride !== undefined
      ? search(active + stride, stride)
      : event.key === "Home"
      ? search(0, 1)
      : event.key === "End"
      ? search(count - 1, -1)
      : null;

    if (target === null) return;
    event.preventDefault();
    setFocused(target);
    nodes.current[target]?.focus();
  };

  const itemProps = (index: number): RovingItemProps => ({
    tabIndex: index === active ? 0 : -1,
    // Wherever focus arrives, that is the key the arrow keys carry on from.
    onFocus: () => setFocused(index),
    ref: (node: HTMLElement | null) => {
      nodes.current[index] = node;
    },
  });

  // Where a list that has just lost one of its own puts the focus back: that place, or the nearest one still there.
  const focus = (index: number) => {
    const target = search(Math.min(index, count - 1), -1) ?? search(0, 1);
    if (target === null) return;
    setFocused(target);
    nodes.current[target]?.focus();
  };

  return { active, focus, handleKeyDown, itemProps };
}

export interface RovingItemProps {
  tabIndex: number;
  onFocus: () => void;
  ref: (node: HTMLElement | null) => void;
}

export const ALWAYS = () => true;

// A click presses the key without taking the focus, the click event firing either way. On `/calculator` that is what
// keeps Enter meaning equals after somebody has used the mouse; on `/unicode` it is what leaves the caret in the box
// a character was just typed into, rather than on the key that typed it.
export function keepFocus(event: { preventDefault: () => void }) {
  event.preventDefault();
}
